import type {
  AttributeProfileResponse,
  AttributeStateResponse,
  EmployeeOverviewResponse,
  EmployeeProfileResponse,
  PotentialAssessmentResponse,
  ReadinessAssessmentResponse,
  TalentDesignationsResponse,
} from '../../api/types';
import { describeError } from '../../api/client';
import { useApiResource, type ResourceState } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ROLE_FIT_STATUS_COPY } from '../role/roleDisplay';
import { AttributeRadar } from './AttributeRadar';
import { CONFIDENCE_COPY, POTENTIAL_TIER_LABEL, READINESS_LEVEL_LABEL, TRAJECTORY_COPY, bandFor } from './attributeDisplay';
import styles from './EmployeeSnapshot.module.css';

/**
 * The Snapshot — the default landing view for an employee profile
 * (Mission 171, "Snapshot + Detailed Profile Experience"): "what do I need
 * to know about this person in 10-20 seconds", answered entirely from data
 * that already exists and is already authoritative elsewhere on this
 * profile.
 *
 * ---------------------------------------------------------------------
 * A PRESENTATION LAYER, NEVER A SECOND SCORING SYSTEM
 *
 * Every figure here is either read verbatim from `GET .../overview` (top
 * strengths, development areas, whole-profile trajectory, the Role Fit
 * summary projection — `docs/DOMAIN_DECISIONS.md` "Profile Overview Role
 * Fit summary projection") or a plain count of an array the API already
 * returned (`attributes.length`, `topStrengths.length` — the same
 * zero-derivation arithmetic every `FactSection` summary line in this
 * application already does). Nothing here averages, weights or combines
 * Rating, Confidence, Role Fit, Potential, Readiness or Trajectory into one
 * figure — each keeps exactly the shape and the name it has everywhere
 * else in this product (§2 of the mission brief, `docs/PRODUCT_CONSTITUTION.md`
 * §8/§12's "never merged" rule).
 *
 * THIS COMPONENT SUPERSEDES `OverviewSection` — NOT BESIDE IT
 *
 * `OverviewSection.tsx` (Mission 020, `docs/UX_ARCHITECTURE.md` §3.2) was
 * "the who-is-this-person summary, built entirely from data that exists
 * elsewhere" — exactly this component's own job description, one mission
 * later. Keeping both would have meant two near-identical summaries on the
 * same person (the Overview card atop Detailed, and this one as Snapshot),
 * which is precisely the "avoid duplicate information" §9 of the mission
 * brief asks for — so `OverviewSection` was retired, not duplicated, and
 * its `GET .../overview` endpoint moved here unchanged.
 *
 * THREE INDEPENDENT REQUESTS, THE SAME DISCIPLINE EVERY OTHER SECTION USES
 *
 * `overview`, `potential`, and the Judgements strip's own `readiness`/
 * `talent-designations` are each this component's own `useApiResource`
 * call, exactly as `OverviewSection`/`PotentialReadinessSection`/
 * `TalentDesignationSection` already fetch independently in Detailed —
 * `attributes` is the one exception, passed down from
 * `EmployeeProfilePage` because Detailed mode needs that exact same
 * request too, and fetching it twice (once per mode) would be the
 * duplicate request §19 of the mission brief asks to avoid.
 * ---------------------------------------------------------------------
 */
export function EmployeeSnapshot({
  profile,
  attributes,
}: {
  profile: EmployeeProfileResponse;
  attributes: ResourceState<AttributeProfileResponse>;
}) {
  const overview = useApiResource<EmployeeOverviewResponse>(`/employees/${profile.id}/overview`);

  if (overview.status === 'loading' || attributes.status === 'loading') {
    return <LoadingState label="Loading snapshot…" />;
  }

  if (overview.status === 'failed') {
    return <ErrorState title="Snapshot could not be loaded" detail={describeError(overview.error)} />;
  }

  const data = overview.data;
  const attributeList = attributes.status === 'loaded' ? attributes.data.attributes : null;
  const assessedCount = attributeList?.filter((a) => a.composite !== null).length ?? 0;

  return (
    <div className={styles.grid}>
      <SummaryStrip data={data} attributeCount={attributeList?.length ?? 0} assessedCount={assessedCount} />
      <RoleFitCard profile={profile} roleFit={data.roleFit} />
      <AttributeList title="Top strengths" attributes={data.topStrengths} employeeId={profile.id} emptyText="No attribute has a genuine assessment yet." />
      <section className={styles.panel} style={{ gridArea: 'radar' }} aria-labelledby="snapshot-attribute-profile-heading">
        <h2 id="snapshot-attribute-profile-heading" className={styles.panelHeading}>
          Attribute profile
        </h2>
        {attributes.status === 'failed' ? (
          <ErrorState title="Attribute profile could not be loaded" detail={describeError(attributes.error)} />
        ) : (
          <AttributeRadar attributes={attributeList!} />
        )}
        <a className={styles.panelLink} href={`/people/${profile.id}/detailed#attributes`}>
          View every attribute →
        </a>
      </section>
      <AttributeList
        title="Development areas"
        attributes={data.developmentAreas}
        employeeId={profile.id}
        emptyText="No attribute currently falls in the Low band among what has been assessed."
        emptyIsGood
      />
      <TrajectoryCard trajectory={data.trajectory} employeeId={profile.id} />
      <JudgementsStrip profile={profile} />
    </div>
  );
}

function SummaryStrip({
  data,
  attributeCount,
  assessedCount,
}: {
  data: EmployeeOverviewResponse;
  attributeCount: number;
  assessedCount: number;
}) {
  const trajectoryCopy = TRAJECTORY_COPY[data.trajectory.direction];

  return (
    <div className={styles.summaryStrip} style={{ gridArea: 'summary' }} role="list" aria-label="Profile summary">
      <div className={styles.summaryChip} role="listitem">
        <span className={styles.summaryValue}>{data.topStrengths.length}</span>
        <span className={styles.summaryLabel}>{data.topStrengths.length === 1 ? 'strength' : 'strengths'}</span>
      </div>
      <div className={styles.summaryChip} role="listitem">
        <span className={styles.summaryValue}>{data.developmentAreas.length}</span>
        <span className={styles.summaryLabel}>development {data.developmentAreas.length === 1 ? 'area' : 'areas'}</span>
      </div>
      <div className={styles.summaryChip} role="listitem">
        <span className={styles.summaryValue} aria-hidden="true">
          {trajectoryCopy.glyph}
        </span>
        <span className={styles.summaryLabel}>{trajectoryCopy.label.toLowerCase()} trajectory</span>
      </div>
      <div className={styles.summaryChip} role="listitem">
        <span className={styles.summaryValue}>
          {assessedCount}/{attributeCount}
        </span>
        <span className={styles.summaryLabel}>attributes assessed</span>
      </div>
    </div>
  );
}

function RoleFitCard({ profile, roleFit }: { profile: EmployeeProfileResponse; roleFit: EmployeeOverviewResponse['roleFit'] }) {
  return (
    <section className={styles.panel} style={{ gridArea: 'rolefit' }} aria-labelledby="snapshot-role-fit-heading">
      <h2 id="snapshot-role-fit-heading" className={styles.panelHeading}>
        Role fit
      </h2>
      {roleFit ? (
        <>
          <p className={styles.roleFitFigure}>
            {roleFit.overallFit}
            <span className={styles.roleFitPercent}>%</span>
          </p>
          <p className={styles.roleFitStatus}>{ROLE_FIT_STATUS_COPY[roleFit.status]} fit</p>
          {roleFit.hasCriticalGap ? <p className={styles.criticalGap}>At least one required item is not met.</p> : null}
          <a className={styles.panelLink} href={`/people/${profile.id}/detailed#role-fit`}>
            View Role Fit breakdown →
          </a>
        </>
      ) : (
        <p className={styles.absent}>
          No current role is recorded, so there is nothing to compare against — an absence of a recorded role
          assignment, not a finding about this person.
        </p>
      )}
    </section>
  );
}

function AttributeList({
  title,
  attributes,
  employeeId,
  emptyText,
  emptyIsGood,
}: {
  title: string;
  attributes: AttributeStateResponse[];
  employeeId: string;
  emptyText: string;
  emptyIsGood?: boolean;
}) {
  const gridArea = title === 'Top strengths' ? 'strengths' : 'development';
  return (
    <section className={styles.panel} style={{ gridArea }} aria-labelledby={`snapshot-${gridArea}-heading`}>
      <h2 id={`snapshot-${gridArea}-heading`} className={styles.panelHeading}>
        {title}
      </h2>
      {attributes.length === 0 ? (
        <p className={emptyIsGood ? styles.absentGood : styles.absent}>
          {emptyIsGood ? 'No current development areas. ' : ''}
          {emptyText}
        </p>
      ) : (
        <ul className={styles.attrRows}>
          {attributes.map((state) => (
            <li key={state.attribute.id} className={styles.attrRow}>
              <span
                className={styles.attrBand}
                aria-hidden="true"
                style={{ background: `var(--band-${bandFor(state.composite!.composite).index})` }}
              />
              <span className={styles.attrName}>{state.attribute.name}</span>
              <span className={styles.attrValue}>{state.composite!.composite.toFixed(1)}</span>
              <span className={styles.attrConfidence} data-confidence={state.confidence.level}>
                {CONFIDENCE_COPY[state.confidence.level].label}
              </span>
            </li>
          ))}
        </ul>
      )}
      <a className={styles.panelLink} href={`/people/${employeeId}/detailed#attributes`}>
        View in Attributes →
      </a>
    </section>
  );
}

function TrajectoryCard({ trajectory, employeeId }: { trajectory: EmployeeOverviewResponse['trajectory']; employeeId: string }) {
  const copy = TRAJECTORY_COPY[trajectory.direction];
  return (
    <section className={styles.panel} style={{ gridArea: 'trajectory' }} aria-labelledby="snapshot-trajectory-heading">
      <h2 id="snapshot-trajectory-heading" className={styles.panelHeading}>
        Trajectory
      </h2>
      {trajectory.direction === 'insufficient_data' ? (
        <p className={styles.absent}>Not enough assessment history yet to state a direction.</p>
      ) : (
        <>
          <p className={styles.trajectoryLine}>
            <span aria-hidden="true" className={styles.trajectoryGlyph} data-direction={trajectory.direction}>
              {copy.glyph}
            </span>
            {copy.label}
          </p>
          <p className={styles.trajectoryDetail}>
            {trajectory.improvingCount} improving · {trajectory.stableCount} stable · {trajectory.decliningCount} declining
            {trajectory.insufficientDataCount > 0 ? ` · ${trajectory.insufficientDataCount} insufficient data` : ''}
          </p>
        </>
      )}
      <a className={styles.panelLink} href={`/people/${employeeId}/detailed#attributes`}>
        View assessment history →
      </a>
    </section>
  );
}

/**
 * Potential, Readiness and Talent Designation — the optional secondary
 * information §6J of the mission brief allows, kept deliberately compact
 * (a label each, nothing more) and read-only: Snapshot never offers a
 * write affordance anywhere, Detailed remains the only place any of these
 * three is recorded or corrected. Fetched here, not lifted to
 * `EmployeeProfilePage`, for the same "each section owns its own request"
 * reasoning as `overview` above — and so Detailed mode (which reads these
 * through its own already-existing `PotentialReadinessSection`/
 * `TalentDesignationSection`) never pays for a request this component also
 * made.
 */
function JudgementsStrip({ profile }: { profile: EmployeeProfileResponse }) {
  const currentRole = profile.currentRole;
  const potential = useApiResource<PotentialAssessmentResponse | null>(`/employees/${profile.id}/potential`);
  const readiness = useApiResource<ReadinessAssessmentResponse | null>(
    currentRole ? `/employees/${profile.id}/readiness/${currentRole.id}` : null
  );
  const designations = useApiResource<TalentDesignationsResponse>(`/employees/${profile.id}/talent-designations`);

  const designationLabels =
    designations.status === 'loaded' ? designations.data.designations.filter((group) => group.activeRecords.length > 0).map((group) => group.label) : [];

  const hasAnything =
    (potential.status === 'loaded' && potential.data !== null) ||
    (readiness.status === 'loaded' && readiness.data !== null) ||
    designationLabels.length > 0;

  if (!hasAnything) return null;

  return (
    <div className={styles.judgements} style={{ gridArea: 'judgements' }} aria-label="Potential, readiness and designation">
      {potential.status === 'loaded' && potential.data ? (
        <a className={styles.judgementPill} href={`/people/${profile.id}/detailed#potential`}>
          Potential: {POTENTIAL_TIER_LABEL[potential.data.potentialTier]}
        </a>
      ) : null}
      {readiness.status === 'loaded' && readiness.data ? (
        <a className={styles.judgementPill} href={`/people/${profile.id}/detailed#readiness`}>
          Readiness: {READINESS_LEVEL_LABEL[readiness.data.readiness]}
        </a>
      ) : null}
      {designationLabels.map((label) => (
        <a key={label} className={styles.judgementPill} href={`/people/${profile.id}/detailed#talent-designation`}>
          {label}
        </a>
      ))}
    </div>
  );
}
